import mapXBackgroundTaskAPI from "api/backgroundTaskApi";
import {
	backgroundTaskApiParameterOptions,
	STBackgroundTaskResponse,
	TImportDataInitialize,
	TImportUrlInfo,
	TLinkedinBulkImportInfo,
} from "api/backgroundTaskApi/types";
import {STProject} from "api/projectApi/types";
import axios, {AxiosResponse, CancelToken, CancelTokenSource} from "axios";
import {withImportErrorReason} from "helpers/importErrorReason";
import {successResponse} from "helpers/map";
import {
	setBackgroundBulkImportsInProgress,
	setProjectCandidateAssessmentProcess,
} from "store/background-tasks/backgroundTaskActions";
import {
	removeBulkImportDataForSource,
	resetBulkImportStatesForProject,
	setBulkImportStateFromBackgroundInProgressData,
	setLastCompleteCompanyImportForProject,
	setLastCompleteImportLinkedInProfileInfoForProject,
	setShowLastImportReport,
} from "store/bulk-import/bulkImportActions";
import {projectSelector} from "store/project-list/projectListSelectors";
import {TAppDispatch, TRootState} from "types";

const cancelTokens: {[key: string]: CancelTokenSource} = {};

/**
 * Dispatches an action to update the state with bulk import information.
 * @param response - API response containing LinkedIn bulk import info.
 * @param urls - List of LinkedIn profile URLs being imported.
 * @param activeProjectInState - The currently active project.
 */
const setBulkImportStates =
	(
		response: AxiosResponse<TLinkedinBulkImportInfo>,
		urls: string[],
		activeProjectInState: STProject,
	) =>
	async (dispatch: TAppDispatch) => {
		const payload = {urls, activeProjectInState, response};

		dispatch(setBulkImportStateFromBackgroundInProgressData(payload));
	};

/**
 * Handles any bulk imports that are still in progress.
 * Fetches their latest status from the API and updates the Redux store.
 * @param inProgressImports - Array of import objects that are still in progress.
 * @param activeProjectInState - The currently active project.
 * @param config - Axios request config, including a cancel token.
 */
const handleInProgressImports =
	(
		inProgressImports: {id: number}[],
		activeProjectInState: STProject,
		config: {cancelToken: CancelToken},
	) =>
	async (dispatch: TAppDispatch) => {
		try {
			// Fetches background task status by ID
			const response: AxiosResponse = await mapXBackgroundTaskAPI.getBackgroundTasksById(
				inProgressImports[0].id,
				config,
			);

			if (response.status === 200) {
				// Extracts URLs of candidates being imported
				const urls = (
					response.data?.linkedin_candidate_urls ||
					response.data?.linkedin_company_urls ||
					[]
				).map((u: TImportUrlInfo) => u.url);

				// Dispatches action to update state with bulk import progress
				await dispatch(setBulkImportStates(response, urls, activeProjectInState));
			}

			dispatch(setBackgroundBulkImportsInProgress(false));
		} catch (error) {
			console.error("Error fetching in-progress import:", error);
		}
	};

/**
 * Handles status updates for each imported LinkedIn profile URL.
 * A failed URL gets a reason message, naming why it failed when the API says (error_reason).
 * @param urlInfos - Array of LinkedIn profile import objects.
 */
const handleUrlStatuses = (urlInfos: TImportUrlInfo[]): TImportUrlInfo[] => {
	const scrapingErrorMessage = "This url could not be processed";

	return urlInfos.map((urlInfo) => {
		const status = urlInfo?.status;

		if (["Import Error", "Scraping Error"].includes(status)) {
			urlInfo.reason = withImportErrorReason(scrapingErrorMessage, urlInfo.error_reason);
		} else if (status === "Not Found") {
			urlInfo.reason = "This profile was not found";
		}

		return urlInfo;
	});
};

/**
 * Handles completed imports and updates the Redux store accordingly.
 * It also merges multiple import results if needed.
 * @param purpose - Can be either "download" (returns data) or "import" (dispatches state updates).
 * @param completedImports - List of completed import objects.
 * @param activeProjectInState - The currently active project.
 * @param config - Axios request configuration (optional).
 */
export const handleCompletedImports =
	(
		purpose: "download" | "import",
		completedImports: {id: number}[],
		activeProjectInState: STProject,
		config?: {cancelToken: CancelToken},
	) =>
	async (dispatch: TAppDispatch) => {
		try {
			const importIDsForCurrentProject = completedImports.map((importItem) => importItem.id);

			if (importIDsForCurrentProject.length === 0) {
				dispatch(setBackgroundBulkImportsInProgress(false));

				return;
			}

			// Fetches details of all completed imports using the batch API
			const response = await mapXBackgroundTaskAPI.getBatchBackgroundTasks(
				importIDsForCurrentProject,
				config,
			);

			if (response?.status === 200 || response?.status === 201) {
				const responseData = response.data.results;

				// Determine which URL field to use based on task type
				const urls = responseData
					.map(
						(item: TLinkedinBulkImportInfo) =>
							item.linkedin_company_urls || item.linkedin_candidate_urls || [],
					)
					.flat();

				// Fixes incorrect status values before storing the data
				const processedUrls = handleUrlStatuses(urls || []);

				const isCompanyImport = completedImports.some(
					(i) => (i as STBackgroundTaskResponse).type === "Companies Bulk Import",
				);

				const mergedData: TImportDataInitialize = {
					id: activeProjectInState.id,
					status: "Completed",
					importInfo: {
						status: responseData.status || "Completed",
						...(isCompanyImport
							? {linkedin_company_urls: processedUrls}
							: {linkedin_candidate_urls: processedUrls}),
					},
				};

				if (purpose === "download") {
					return mergedData;
				}

				const payload = {id: mergedData.id, data: mergedData};

				// Updates the store with the latest completed import
				if (isCompanyImport) {
					dispatch(setLastCompleteCompanyImportForProject(payload));
				} else {
					dispatch(setLastCompleteImportLinkedInProfileInfoForProject(payload));
				}
			}

			dispatch(setBackgroundBulkImportsInProgress(false));
		} catch (error) {
			console.error("Error fetching completed imports:", error);
			dispatch(setBackgroundBulkImportsInProgress(false));
		}
	};
// Function to fetch all background bulk imports for the active project
export const getAllBackgroundBulkImports =
	() => async (dispatch: TAppDispatch, getState: () => TRootState) => {
		const state = getState();

		const activeProjectInState = projectSelector(state) as STProject;

		// Check if an import or refresh operation is already in progress
		// const importInProgress = isImportOrRefreshInProgressForCurrentProject(state);

		// if (!importInProgress) {
		const requestKey = `GET_BACKGROUND_BULK_IMPORTS`;

		if (requestKey in cancelTokens) {
			cancelTokens[requestKey].cancel("Operation canceled due to new request.");
		}

		cancelTokens[requestKey] = axios.CancelToken.source();

		const config = {
			cancelToken: cancelTokens[requestKey].token,
		};

		dispatch(setBackgroundBulkImportsInProgress(true));

		if (activeProjectInState) {
			const payload: backgroundTaskApiParameterOptions = {
				projectId: activeProjectInState.id,
				status: ["Completed", "In Progress", "Created"],
				backgroundTaskType: [
					"Candidates Bulk Import",
					"Companies Bulk Import",
					"Candidate Assessment",
				],
			};

			const pagination = {
				page: 1,
				perPage: 20,
			};

			try {
				const response: AxiosResponse =
					await mapXBackgroundTaskAPI.getAllBackgroundTaskRequests(
						payload,
						pagination,
						config,
					);

				if (successResponse(response, 200)) {
					const backgroundTasks: STBackgroundTaskResponse[] = response.data.results;

					// Separate bulk import tasks from candidate assessments
					const bulkImportTasks = backgroundTasks.filter(
						(p: STBackgroundTaskResponse) =>
							p.type === "Candidates Bulk Import" ||
							p.type === "Companies Bulk Import",
					);

					const inProgressAssessments = backgroundTasks.filter(
						(p: STBackgroundTaskResponse) => p.type === "Candidate Assessment",
					);

					// If there are bulk import tasks, process them
					if (bulkImportTasks.length > 0) {
						dispatch(
							processBackgroundBulkImportsFromResponse(
								bulkImportTasks,
								activeProjectInState,
							),
						);
					} else {
						// If no bulk import tasks, set import progress to false
						dispatch(setBackgroundBulkImportsInProgress(false));
					}

					// If there are candidate assessment tasks in progress, process them
					if (inProgressAssessments.length > 0) {
						dispatch(
							processProjectCandidateInProgressAssessment(inProgressAssessments),
						);
					}
				} else {
					// If response is unsuccessful, hide the last import report and stop progress indicator
					dispatch(setShowLastImportReport(false));
					dispatch(setBackgroundBulkImportsInProgress(false));
				}
			} catch (error) {
				console.error("Error fetching background bulk imports:", error);
				dispatch(setBackgroundBulkImportsInProgress(false));
			}
		}
		// }
	};

// Function to process candidate assessment tasks that are still in progress
export const processProjectCandidateInProgressAssessment =
	(tasks: STBackgroundTaskResponse[]) => async (dispatch: TAppDispatch) => {
		try {
			// Filter candidate assessment tasks that are still in progress or newly created
			const inProgressTasks = tasks.filter(
				(p: STBackgroundTaskResponse) =>
					p.type === "Candidate Assessment" &&
					(p.status === "In Progress" || p.status === "Created"),
			);

			if (inProgressTasks?.length > 0) {
				const requestKey = `HANDLE_IN_PROGRESS_ASSESSMENTS`;

				if (requestKey in cancelTokens) {
					cancelTokens[requestKey].cancel("Operation canceled due to new request.");
				}

				cancelTokens[requestKey] = axios.CancelToken.source();

				const config = {
					cancelToken: cancelTokens[requestKey].token,
				};

				// Get the most recent in-progress task
				const latestInProgressTask = inProgressTasks[0];

				// Fetch additional task details from API
				const data = (await dispatch(
					getBackgroundTaskInfoById(latestInProgressTask.id, config),
				)) as unknown as Nullable<STBackgroundTaskResponse>;

				// If the data is valid, update the Redux state with the candidate assessment process
				if (data !== null) {
					dispatch(setProjectCandidateAssessmentProcess(latestInProgressTask));
				}
			}
		} catch (e) {
			console.error(e);
		}
	};

// Function to process bulk import tasks retrieved from the API
export const processBackgroundBulkImportsFromResponse =
	(bulkImportBackgroundTasks: STBackgroundTaskResponse[], project: STProject) =>
	async (dispatch: TAppDispatch) => {
		// Identify tasks that are still in progress or newly created
		const inProgressImports = bulkImportBackgroundTasks.filter(
			(p: STBackgroundTaskResponse) =>
				(p.type === "Candidates Bulk Import" || p.type === "Companies Bulk Import") &&
				(p.status === "In Progress" || p.status === "Created"),
		);

		// Find the latest 5 completed Candidate imports
		const completedCandidateImports = bulkImportBackgroundTasks
			.filter(
				(c: STBackgroundTaskResponse) =>
					c.type === "Candidates Bulk Import" && c.status === "Completed",
			)
			.sort((a, b) => b.id - a.id)
			.slice(0, 5);

		// Find the latest 5 completed Company imports
		const completedCompanyImports = bulkImportBackgroundTasks
			.filter(
				(c: STBackgroundTaskResponse) =>
					c.type === "Companies Bulk Import" && c.status === "Completed",
			)
			.sort((a, b) => b.id - a.id)
			.slice(0, 5);

		const requestKey = `HANDLE_IN_PROGRESS_IMPORTS`;

		if (requestKey in cancelTokens) {
			cancelTokens[requestKey].cancel("Operation canceled due to new request.");
		}

		cancelTokens[requestKey] = axios.CancelToken.source();

		const config = {
			cancelToken: cancelTokens[requestKey].token,
		};

		// If there are in-progress imports, process them
		if (inProgressImports.length > 0) {
			dispatch(setShowLastImportReport(false));
			await dispatch(handleInProgressImports(inProgressImports, project, config));
		}

		// Handle Candidate Completed Imports
		if (completedCandidateImports.length > 0) {
			if (inProgressImports.length === 0) {
				dispatch(removeBulkImportDataForSource(project.id));
				dispatch(resetBulkImportStatesForProject(project.id));
				dispatch(setShowLastImportReport(true));
			}
			await dispatch(
				handleCompletedImports("import", completedCandidateImports, project, config),
			);
		}

		// Handle Company Completed Imports
		if (completedCompanyImports.length > 0) {
			// We don't reset global states here to avoid clashing with candidates,
			// just fetch and set company history.
			await dispatch(
				handleCompletedImports("import", completedCompanyImports, project, config),
			);
		}

		if (
			inProgressImports.length === 0 &&
			completedCandidateImports.length === 0 &&
			completedCompanyImports.length === 0
		) {
			dispatch(setBackgroundBulkImportsInProgress(false));
		}
	};

// Function to fetch details of a specific background task by its ID
export const getBackgroundTaskInfoById =
	(taskId: number, config = {}) =>
	async () => {
		try {
			const response = await mapXBackgroundTaskAPI.getBackgroundTasksById(taskId, config);

			if (response && successResponse(response, 200)) {
				return response.data;
			}
		} catch (error) {
			console.error("Error fetching in-progress import:", error);
		}

		return null;
	};
